import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "hiddenInput", "specialDatesContainer"]

  connect() {
    this.days = [1, 2, 3, 4, 5, 6, 0]
    
    let hiddenInputValue = this.hiddenInputTarget?.value
    let parsedHiddenInput = null
    try {
      if (hiddenInputValue && hiddenInputValue.trim() !== "") {
        parsedHiddenInput = JSON.parse(hiddenInputValue)
      }
    } catch (e) {
      parsedHiddenInput = null
    }

    this.supportTimes = parsedHiddenInput && Object.keys(parsedHiddenInput).length > 0
      ? parsedHiddenInput
      : this.createDefaultSchedule()

    if (!this.supportTimes.specialDates) {
      this.supportTimes.specialDates = {}
    }
    
    this.render()
    this.renderSpecialDates()
    this.updateResult()
  }

  createDefaultSchedule() {
    const schedule = {}
    this.days.forEach(day => {
      // Closed per default on weekends
      if (day === 6 || day === 0) {
        schedule[day] = {
          status: 'closed',
          slots: []
        }
      } else {
        schedule[day] = {
          status: 'open',
          slots: [{ from: '09:00', to: '18:00' }]
        }
      }
    })
    schedule.specialDates = {}
    return schedule
  }

  render() {
    this.containerTarget.innerHTML = ''
    this.days.forEach(day => {
      const dayElement = this.createDaySection(day)
      this.containerTarget.appendChild(dayElement)
    })
    
    this.addSpecialDatesSection()
  }

  createDaySection(day) {
    const section = document.createElement('div')
    section.className = 'day-section'
    section.dataset.day = day

    const header = this.createDayHeader(day)
    const slots = this.createTimeSlotsSection(day)
    
    section.appendChild(header)
    section.appendChild(slots)
    
    return section
  }

  createDayHeader(day) {
    const header = document.createElement('div')
    header.className = 'day-header'

    const dayName = document.createElement('div')
    dayName.className = 'day-name'
    dayName.textContent = window.I18n.support_times.days[day]

    const controls = document.createElement('div')
    controls.className = 'day-controls'

    const toggleGroup = this.createToggleGroup(day)
    const statusIndicator = this.createStatusIndicator(day)

    controls.appendChild(toggleGroup)
    controls.appendChild(statusIndicator)
    
    header.appendChild(dayName)
    header.appendChild(controls)
    
    return header
  }

  createToggleGroup(day) {
    const group = document.createElement('div')
    group.className = 'btn-group'

    const buttons = [
      { text: window.I18n.support_times.open, status: 'open' },
      { text: window.I18n.support_times.allday, status: 'allday' },
      { text: window.I18n.support_times.closed, status: 'closed' }
    ]

    buttons.forEach(({ text, status }) => {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'btn toggle-btn'
      btn.textContent = text
      btn.dataset.action = 'click->support-times#setDayStatus'
      btn.dataset.day = day
      btn.dataset.status = status
      
      if (this.supportTimes[day].status === status) {
        btn.classList.add('active')
      }
      
      group.appendChild(btn)
    })

    return group
  }

  createStatusIndicator(day) {
    const indicator = document.createElement('span')
    indicator.className = 'status-indicator'
    indicator.dataset.target = 'support-times.statusIndicator'
    indicator.dataset.day = day
    return indicator
  }

  createTimeSlotsSection(day) {
    const section = document.createElement('div')
    section.className = `time-slots ${this.supportTimes[day].status === 'open' ? 'active' : ''}`
    section.dataset.day = day
    section.dataset.target = 'support-times.timeSlotsContainer'
    
    this.renderTimeSlots(section, day)
    return section
  }

  renderTimeSlots(container, day) {
    container.innerHTML = ''
    
    this.supportTimes[day].slots.forEach((slot, index) => {
      const slotElement = this.createTimeSlot(day, index)
      container.appendChild(slotElement)
    })
  }

  createTimeSlot(day, index) {
    const slot = this.supportTimes[day].slots[index]
    const container = document.createElement('div')
    container.className = 'time-slot'
    container.dataset.slot = index

    container.innerHTML = `
      <span>${window.I18n.support_times.from}</span>
      <input type="time" class="time-input" value="${slot.from}" max="23:58"
             data-action="change->support-times#updateTimeSlot"
             data-day="${day}" data-index="${index}" data-field="from">
      <span>${window.I18n.support_times.to}</span>
      <input type="time" class="time-input" value="${slot.to}" min="${this.adjustTimeByMinutes(slot.from, 1)}"
             data-action="change->support-times#updateTimeSlot" 
             data-day="${day}" data-index="${index}" data-field="to">
      ${index > 0 ? `
        <button type="button" class="btn btn-action ti ti-trash"
                data-action="click->support-times#removeTimeSlot"
                data-day="${day}" data-index="${index}"></button>` : `
        <button type="button" class="btn btn-action ti ti-plus"
                data-action="click->support-times#addTimeSlot"
                data-day="${day}" data-index="${index}"></button>
      `}
    `

    return container
  }

  setDayStatus(event) {
    const { day, status } = event.currentTarget.dataset
    this.supportTimes[day].status = status
    
    if (status !== 'open') {
      this.supportTimes[day].slots = []
    } else if (this.supportTimes[day].slots.length === 0) {
      // Add default slot when changing to open and no slots exist
      this.supportTimes[day].slots = [{ from: '09:00', to: '18:00' }]
    }
    
    const daySection = this.containerTarget.querySelector(`[data-day="${day}"]`)
    const toggleBtns = daySection.querySelectorAll('.toggle-btn')
    toggleBtns.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.status === status)
    })

    const slotsContainer = daySection.querySelector('.time-slots')
    
    if (status === 'open') {
      slotsContainer.classList.add('active')
      this.renderTimeSlots(slotsContainer, day)
    } else {
      slotsContainer.classList.remove('active')
    }

    this.updateResult()
  }

  addTimeSlot(event) {
    const day = event.currentTarget.dataset.day
    this.supportTimes[day].slots.push({ from: '12:00', to: '14:00' })
    
    const daySection = this.containerTarget.querySelector(`[data-day="${day}"]`)
    const slotsContainer = daySection.querySelector('.time-slots')
    this.renderTimeSlots(slotsContainer, day)
    
    this.updateResult()
  }

  removeTimeSlot(event) {
    const { day, index } = event.currentTarget.dataset
    this.supportTimes[day].slots.splice(parseInt(index), 1)
    
    const daySection = this.containerTarget.querySelector(`[data-day="${day}"]`)
    const slotsContainer = daySection.querySelector('.time-slots')
    this.renderTimeSlots(slotsContainer, day)
    
    this.updateResult()
  }

  updateTimeSlot(event) {
    const { day, index, field } = event.currentTarget.dataset
    const value = event.currentTarget.value

    this.supportTimes[day].slots[parseInt(index)][field] = value

    this.updateTimeConstraints(event.currentTarget, field, value)

    this.updateResult()
  }

  updateResult() {
    // Get cleaned data for output
    const cleanedData = this.getCleanedData()
    
    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = JSON.stringify(cleanedData)
    }
  }

  getCleanedData() {
    // Create a deep copy and clean up all slots
    const cleanedData = JSON.parse(JSON.stringify(this.supportTimes))
    
    // Clean up regular days
    this.days.forEach(day => {
      if (cleanedData[day] && cleanedData[day].status === 'open' && cleanedData[day].slots.length > 0) {
        cleanedData[day].slots = this.cleanupSlots(cleanedData[day].slots)
      }
    })
    
    // Clean up special dates
    Object.values(cleanedData.specialDates).forEach(specialDate => {
      if (specialDate.status === 'open' && specialDate.slots.length > 0) {
        specialDate.slots = this.cleanupSlots(specialDate.slots)
      }
    })
    
    return cleanedData
  }

  cleanupSlots(slots) {
    if (!slots || slots.length <= 1) return slots
    
    // Sort slots by start time
    const sortedSlots = [...slots].sort((a, b) => a.from.localeCompare(b.from))
    const cleanedSlots = []
    
    let currentSlot = { ...sortedSlots[0] }
    
    for (let i = 1; i < sortedSlots.length; i++) {
      const nextSlot = sortedSlots[i]
      
      // Check if slots overlap or are adjacent
      if (currentSlot.to >= nextSlot.from) {
        // Merge slots - extend current slot to the later end time
        currentSlot.to = currentSlot.to > nextSlot.to ? currentSlot.to : nextSlot.to
      } else {
        // No overlap, add current slot and move to next
        cleanedSlots.push(currentSlot)
        currentSlot = { ...nextSlot }
      }
    }
    
    // Add the last slot
    cleanedSlots.push(currentSlot)
    
    return cleanedSlots
  }

  addSpecialDatesSection() {
    const section = document.createElement('div')
    section.className = 'day-section flex-column border-bottom-0'
    
    const header = document.createElement('div')
    header.innerHTML = `
      <h2 class="mt-5">${window.I18n.support_times.special_dates.title}</h1>
      <p class="mb-3 ms-3">${window.I18n.support_times.special_dates.description}</p>
    `
    
    const controls = document.createElement('div')
    controls.className = 'day-controls ms-3'
    
    const addButton = document.createElement('button')
    addButton.type = 'button'
    addButton.className = 'btn btn-icon ti ti-plus'
    addButton.dataset.action = 'click->support-times#addSpecialDate'
    
    controls.appendChild(addButton)
    section.appendChild(header)

    const container = document.createElement('div')
    container.className = 'special-dates-container'
    container.dataset.target = 'support-times.specialDatesContainer'
    section.appendChild(container)
    
    section.appendChild(controls)

    this.containerTarget.appendChild(section)
    
    this.renderSpecialDates()
  }

  renderSpecialDates() {
    if (!this.hasSpecialDatesContainerTarget) return
    
    this.specialDatesContainerTarget.innerHTML = ''
    
    Object.entries(this.supportTimes.specialDates).forEach(([date, specialDate]) => {
      const dateElement = this.createSpecialDateElement(date, specialDate)
      this.specialDatesContainerTarget.appendChild(dateElement)
    })
  }

  createSpecialDateElement(date, specialDate) {
    const section = document.createElement('div')
    section.className = 'day-section'
    section.dataset.date = date

    const header = this.createSpecialDateHeader(date, specialDate)
    const slots = this.createSpecialTimeSlotsSection(date, specialDate)
    
    section.appendChild(header)
    section.appendChild(slots)
    
    return section
  }

  createSpecialDateHeader(date, specialDate) {
    const header = document.createElement('div')
    header.className = 'day-header'
    
    const dateInput = document.createElement('div')
    dateInput.className = 'day-name'
    dateInput.innerHTML = `
      <input type="date" value="${date}" class="form-control mb-3"
             data-action="change->support-times#updateSpecialDate" 
             data-date="${date}" data-field="date">
    `

    const controls = document.createElement('div')
    controls.className = 'day-controls'

    const toggleGroup = this.createSpecialDateToggleGroup(date, specialDate)
    const deleteButton = document.createElement('button')
    deleteButton.type = 'button'
    deleteButton.className = 'btn btn-action ti ti-trash ms-3'
    deleteButton.dataset.action = 'click->support-times#removeSpecialDate'
    deleteButton.dataset.date = date

    controls.appendChild(toggleGroup)
    dateInput.appendChild(deleteButton)

    header.appendChild(dateInput)
    header.appendChild(controls)
    
    return header
  }

  createSpecialDateToggleGroup(date, specialDate) {
    const group = document.createElement('div')
    group.className = 'btn-group'

    const buttons = [
      { text: window.I18n.support_times.open, status: 'open' },
      { text: window.I18n.support_times.allday, status: 'allday' },
      { text: window.I18n.support_times.closed, status: 'closed' }
    ]

    buttons.forEach(({ text, status }) => {
      const btn = document.createElement('button')
      btn.type = 'button'
      btn.className = 'btn toggle-btn'
      btn.textContent = text
      btn.dataset.action = 'click->support-times#setSpecialDateStatus'
      btn.dataset.date = date
      btn.dataset.status = status
      
      if (specialDate.status === status) {
        btn.classList.add('active')
      }
      
      group.appendChild(btn)
    })

    return group
  }

  createSpecialDateStatusIndicator(date, specialDate) {
    const indicator = document.createElement('span')
    indicator.className = 'status-indicator'
    indicator.dataset.date = date
    this.updateSpecialDateStatusIndicatorText(indicator, specialDate)
    return indicator
  }

  createSpecialTimeSlotsSection(date, specialDate) {
    const section = document.createElement('div')
    section.className = `time-slots ${specialDate.status === 'open' ? 'active' : ''}`
    section.dataset.date = date
    section.dataset.target = 'support-times.specialTimeSlotsContainer'
    
    this.renderSpecialTimeSlots(section, date)
    return section
  }

  updateSpecialDateStatusIndicatorText(indicator, specialDate) {
    if (specialDate.status === 'open' && specialDate.slots.length > 0) {
      const timeRanges = specialDate.slots.map(slot => `${slot.from} - ${slot.to}`).join(', ')
      indicator.textContent = timeRanges
      indicator.className = 'status-indicator open-indicator'
    } else if (specialDate.status === 'closed') {
      indicator.textContent = 'Geschlossen'
      indicator.className = 'status-indicator closed-indicator'
    } else if (specialDate.status === 'allday') {
      indicator.textContent = '24 Stunden geöffnet'
      indicator.className = 'status-indicator open-indicator'
    }
  }

  setSpecialDateStatus(event) {
    const { date, status } = event.currentTarget.dataset
    const specialDate = this.supportTimes.specialDates[date]
    specialDate.status = status
    
    // Clear slots for non-open statuses
    if (status !== 'open') {
      specialDate.slots = []
    } else if (specialDate.slots.length === 0) {
      specialDate.slots = [{ from: '09:00', to: '18:00' }]
    }
    
    // Update toggle buttons
    const daySection = this.specialDatesContainerTarget.querySelector(`[data-date="${date}"]`)
    const toggleBtns = daySection.querySelectorAll('.toggle-btn')
    toggleBtns.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.status === status)
    })

    // Show/hide time slots
    const slotsContainer = daySection.querySelector('.time-slots')
    
    if (status === 'open') {
      slotsContainer.classList.add('active')
      this.renderSpecialTimeSlots(slotsContainer, date)
    } else {
      slotsContainer.classList.remove('active')
    }

    this.renderSpecialDates()
    this.updateResult()
  }

  renderSpecialTimeSlotsHTML(date) {
    const specialDate = this.supportTimes.specialDates[date]
    if (!specialDate || specialDate.status !== 'open') return ''
    
    let html = ''
    specialDate.slots.forEach((slot, slotIndex) => {
      html += `
        <div class="time-slot">
          <span>${window.I18n.support_times.from}</span>
          <input type="time" class="time-input" value="${slot.from}" max="23:58"
                 data-action="change->support-times#updateSpecialTimeSlot"
                 data-date="${date}" data-slot="${slotIndex}" data-field="from">
          <span>${window.I18n.support_times.to}</span>
          <input type="time" class="time-input" value="${slot.to}" min="${this.adjustTimeByMinutes(slot.from, 1)}"
                 data-action="change->support-times#updateSpecialTimeSlot" 
                 data-date="${date}" data-slot="${slotIndex}" data-field="to">
          ${slotIndex > 0 ? `
            <button type="button" class="btn btn-action ti ti-trash"
                    data-action="click->support-times#removeSpecialTimeSlot"
                    data-date="${date}" data-slot="${slotIndex}"></button>` : `
            <button type="button" class="btn btn-action ti ti-plus"
                    data-action="click->support-times#addSpecialTimeSlot"
                    data-date="${date}" data-slot="${slotIndex}"></button>
          `}
        </div>
      `
    })
    return html
  }

  renderSpecialTimeSlots(container, date) {
    container.innerHTML = this.renderSpecialTimeSlotsHTML(date)
  }

  addSpecialDate() {
    // Generate a unique date key that doesn't already exist
    const dateKey = this.generateUniqueDateKey()
    
    const newSpecialDate = {
      status: 'open',
      slots: [{ from: '09:00', to: '18:00' }]
    }
    
    this.supportTimes.specialDates[dateKey] = newSpecialDate
    this.renderSpecialDates()
    this.updateResult()
  }

  generateUniqueDateKey() {
    let dateKey = new Date().toISOString().split('T')[0]
    let counter = 1
    
    while (this.supportTimes.specialDates[dateKey]) {
      const baseDate = new Date().toISOString().split('T')[0]
      const nextDate = new Date(baseDate)
      nextDate.setDate(nextDate.getDate() + counter)
      dateKey = nextDate.toISOString().split('T')[0]
      counter++
    }
    
    return dateKey
  }

  removeSpecialDate(event) {
    const date = event.currentTarget.dataset.date
    delete this.supportTimes.specialDates[date]
    this.renderSpecialDates()
    this.updateResult()
  }

  updateSpecialDate(event) {
    const { date, field } = event.currentTarget.dataset
    const newValue = event.currentTarget.value
    const specialDate = this.supportTimes.specialDates[date]
    
    if (field === 'date') {
      if (this.supportTimes.specialDates[newValue] && newValue !== date) {
        return
      }
      
      delete this.supportTimes.specialDates[date]
      this.supportTimes.specialDates[newValue] = specialDate
      this.renderSpecialDates()
    } else if (field === 'status') {
      specialDate.status = newValue
      if (newValue !== 'open') {
        specialDate.slots = []
      } else if (specialDate.slots.length === 0) {
        specialDate.slots = [{ from: '09:00', to: '18:00' }]
      }
      this.renderSpecialDates()
    } else {
      specialDate[field] = newValue
    }
    
    this.updateResult()
  }

  addSpecialTimeSlot(event) {
    const date = event.currentTarget.dataset.date
    const specialDate = this.supportTimes.specialDates[date]
    
    if (specialDate.status === 'open') {
      specialDate.slots.push({ from: '12:00', to: '14:00' })
      this.renderSpecialDates()
      this.updateResult()
    }
  }

  removeSpecialTimeSlot(event) {
    const { date, slot } = event.currentTarget.dataset
    const specialDate = this.supportTimes.specialDates[date]
    
    specialDate.slots.splice(parseInt(slot), 1)
    this.renderSpecialDates()
    this.updateResult()
  }

  updateSpecialTimeSlot(event) {
    const { date, slot, field } = event.currentTarget.dataset
    const specialDate = this.supportTimes.specialDates[date]

    specialDate.slots[parseInt(slot)][field] = event.currentTarget.value

    this.updateTimeConstraints(event.currentTarget, field, event.currentTarget.value)

    this.updateResult()
  }

  updateTimeConstraints(timeInput, field, newValue) {
    const timeSlot = timeInput.closest('.time-slot')

    if (field === 'from') {
      // Update min attribute on end time field
      const endTimeInput = timeSlot.querySelector('input[data-field="to"]')
      if (endTimeInput) {
        const minEndTime = this.adjustTimeByMinutes(newValue, 1)
        endTimeInput.min = minEndTime
      }
    }
  }

  updateDataModel(timeInput, field, value) {
    if (timeInput.dataset.day) {
      // Regular day slot
      const { day, index } = timeInput.dataset
      this.supportTimes[day].slots[parseInt(index)][field] = value
    } else if (timeInput.dataset.date) {
      // Special date slot
      const { date, slot } = timeInput.dataset
      this.supportTimes.specialDates[date].slots[parseInt(slot)][field] = value
    }
  }

  adjustTimeByMinutes(timeString, adjustmentMinutes) {
    const [hours, minutes] = timeString.split(':').map(Number)
    const date = new Date()
    date.setHours(hours, minutes + adjustmentMinutes, 0, 0)
    return date.toTimeString().slice(0, 5)
  }
}